"use client";

import { useState, useRef, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
    typeÀLOuverture,
    typeAprèsSaisie,
    typeEnregistré,
} from "@/lib/type-de-client";
import { AlertTriangle, ArrowLeft, Building2, Check, ChevronDown, FileText, Languages, Loader2, Mail, MapPin, Phone, Plus, Save, Search, Trash2, User } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useData } from "@/components/data-provider";
import { dataService } from "@/lib/data-service";
import { createClientAction, updateClient } from "@/app/actions";
import { Client, AdresseFacturation } from "@/types";
import { COUNTRIES } from "@/lib/countries";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n/provider";
import { aUneEcritureOriginale, clientAVerifier, champEstVerrouille } from "@/lib/client-utils";
import { decideLaVille, estUnCodePostalFrancais } from "@/lib/ville-du-code-postal";
import { AdresseAutocomplete } from "./AdresseAutocomplete";

interface ClientFormValues {
    type: "societe" | "particulier";
    nom: string;
    siret: string;
    tvaIntra: string;
    email: string;
    telephone: string;
    mobile: string;
    adresse: string;
    codePostal: string;
    ville: string;
    pays: string;
    prenomContact: string;
    nomContact: string;
    /** Version latine, modifiable. Vide ⇒ régénérée automatiquement. */
    nomLatin: string;
    adresseLatin: string;
    villeLatin: string;
    prenomContactLatin: string;
    nomContactLatin: string;
    /** Les AUTRES adresses de facturation — la principale est ci-dessus. */
    adressesFacturation: AdresseFacturation[];
}

export function ClientEditor({ initialData, onSuccess, onCancel }: { initialData?: Client, onSuccess?: (client: Client) => void, onCancel?: () => void }) {
    const { refreshData, societe, setIsDirty, confirm, logAction, clients } = useData();
    const { t } = useTranslation();
    const router = useRouter();
    const searchParams = useSearchParams();
    const returnUrl = searchParams.get("returnUrl");

    const [countryOpen, setCountryOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState(initialData?.pays || "France");
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isSaving, setIsSaving] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Company Search State
    const [searchTerm, setSearchTerm] = useState(initialData?.nom || "");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const { register, control, handleSubmit, setValue, getValues, watch, reset, formState: { errors, isDirty } } = useForm<ClientFormValues>({
        defaultValues: {
            // Le type vient de « typeClient », et de lui seul. L'ancienne
            // règle retombait sur « particulier » quand le SIRET manquait —
            // c'est-à-dire précisément quand on ouvrait la fiche pour le
            // saisir, et le champ n'était alors pas affiché.
            type: initialData ? typeÀLOuverture(initialData) : "societe",
            nom: initialData?.nom || "",
            siret: initialData?.siret || "",
            tvaIntra: initialData?.tvaIntra || "",
            email: initialData?.email || "",
            telephone: initialData?.telephone || "",
            mobile: initialData?.mobile || "",
            adresse: initialData?.adresse || "",
            codePostal: initialData?.codePostal || "",
            ville: initialData?.ville || "",
            pays: initialData?.pays || "France",
            prenomContact: initialData?.prenomContact || "",
            nomContact: initialData?.nomContact || "",
            // Les versions latines telles qu'elles sont EN BASE : automatiques
            // dans le cas courant, saisies à la main si quelqu'un les a
            // corrigées. Vider un de ces champs et enregistrer suffit à le
            // faire régénérer — c'est la seule façon d'annuler une correction,
            // et elle n'a pas besoin d'un second bouton.
            nomLatin: initialData?.nomLatin || "",
            adresseLatin: initialData?.adresseLatin || "",
            villeLatin: initialData?.villeLatin || "",
            prenomContactLatin: initialData?.prenomContactLatin || "",
            nomContactLatin: initialData?.nomContactLatin || "",
            adressesFacturation: (initialData?.adressesFacturation ?? []).map((a) => ({
                id: a.id,
                libelle: a.libelle || "",
                adresse: a.adresse || "",
                adresse2: a.adresse2 || "",
                codePostal: a.codePostal || "",
                ville: a.ville || "",
                pays: a.pays || "France",
            })),
        }
    });

    // Les autres adresses de facturation. Voir « src/lib/adresses-facturation.ts » :
    // la liste ENTIÈRE part au serveur, qui met à jour en place les lignes
    // connues — une adresse visée par des factures garde son identifiant.
    const adresses = useFieldArray({ control, name: "adressesFacturation" });

    // Auto-fill City based on Zip Code (France only)
    const watchedZip = watch("codePostal");
    const watchedName = watch("nom");
    const watchedPrenomContact = watch("prenomContact");
    const watchedNomContact = watch("nomContact");
    const watchedType = watch("type");
    const watchedSiret = watch("siret");
    const watchedTva = watch("tvaIntra");

    /**
     * SAISIR UN SIRET OU UNE TVA FAIT PASSER EN SOCIÉTÉ.
     *
     * C'est ce que ces identifiants signifient : seule une entreprise en a.
     * La promotion est donc automatique.
     *
     * L'inverse ne l'est pas. Effacer un SIRET ne déclasse pas — sans quoi une
     * correction de frappe suffirait à sortir un client de la facturation
     * électronique, et il faudrait s'en apercevoir.
     */
    useEffect(() => {
        const voulu = typeAprèsSaisie(watchedType, watchedSiret, watchedTva);
        if (voulu !== watchedType) setValue("type", voulu, { shouldDirty: true });
    }, [watchedSiret, watchedTva, watchedType, setValue]);

    // Auto-fill 'nom' for INDIVIDUAL type
    useEffect(() => {
        if (watchedType === "particulier") {
            const parts = [];
            if (watchedPrenomContact?.trim()) parts.push(watchedPrenomContact.trim());
            if (watchedNomContact?.trim()) parts.push(watchedNomContact.trim());
            if (parts.length > 0) {
                setValue("nom", parts.join(" "), { shouldDirty: true });
            }
        }
    }, [watchedPrenomContact, watchedNomContact, watchedType, setValue]);

    // Reset success state when name changes
    useEffect(() => {
        if (isSuccess) setIsSuccess(false);
    }, [watchedName, isSuccess]);

    // Check for duplicates
    const duplicateClient = !isSaving && !isSuccess && clients.find(c =>
        c.nom.trim().toLowerCase() === watchedName?.trim().toLowerCase() &&
        c.id !== initialData?.id
    );

    /**
     * LA VILLE D'APRÈS LE CODE POSTAL — sans écraser ce qui est juste.
     *
     * Amine, 19/09/2026 : « je mets des codes postaux, ça ne me met pas les
     * bonnes villes ». L'ancien code prenait la PREMIÈRE commune de l'API et
     * tournait aussi à l'ouverture de la fiche : la ville enregistrée était
     * remplacée. La décision est dans « src/lib/ville-du-code-postal.ts » ;
     * ici on ne fait qu'interroger l'API et proposer la liste.
     */
    const [communesProposees, setCommunesProposees] = useState<string[]>([]);
    const codePostalInitial = useRef(initialData?.codePostal || "");
    useEffect(() => {
        // À l'ouverture d'une fiche existante, le code postal n'a pas changé :
        // rien à faire, la ville enregistrée est la bonne.
        if (watchedZip === codePostalInitial.current) return;
        codePostalInitial.current = "";
        if (!estUnCodePostalFrancais(watchedZip)) { setCommunesProposees([]); return; }

        let annule = false;
        const fetchCity = async () => {
            try {
                const response = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${watchedZip.trim()}&fields=nom&format=json&geometry=centre`);
                if (!response.ok || annule) return;
                const data: { nom: string }[] = await response.json();
                const decision = decideLaVille(getValues("ville"), (data || []).map((c) => c.nom));
                setCommunesProposees(decision.propositions);
                if (decision.ville) setValue("ville", decision.ville, { shouldDirty: true });
                if (decision.propositions.length > 0) {
                    setValue("pays", "France");
                    setCountrySearch("France");
                }
            } catch (error) {
                console.error("Error fetching city:", error);
            }
        };

        const timeoutId = setTimeout(fetchCity, 300);
        return () => { annule = true; clearTimeout(timeoutId); };
    }, [watchedZip, setValue, getValues]);

    // Sync isDirty with global state
    useEffect(() => {
        setIsDirty(isDirty);
        return () => setIsDirty(false);
    }, [isDirty, setIsDirty]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setCountryOpen(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const delayDebounceFn = setTimeout(async () => {
            if (searchTerm.length >= 3 && document.activeElement === searchRef.current?.querySelector('input')) {
                try {
                    setIsSearching(true);
                    setSearchError(null);
                    setShowResults(true);

                    const response = await fetch(`/api/company-search?q=${encodeURIComponent(searchTerm.trim())}`, {
                        signal // Connect the abort signal
                    });
                    
                    const data = await response.json();
                    
                    if (data.success) {
                        setSearchResults(data.results);
                    } else if (data.error) {
                        setSearchResults([]);
                        setSearchError(data.error);
                    } else {
                        setSearchResults([]);
                    }
                } catch (error: any) {
                    if (error.name === 'AbortError') return; // Ignore cancellations
                    console.error("Search error:", error);
                    setSearchError(t('clients.searchContactError'));
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        }, 500);

        return () => {
            clearTimeout(delayDebounceFn);
            controller.abort(); // Cancel the request if user keeps typing
        };
    }, [searchTerm]);

    const handleSelectCompany = (company: any) => {
        setSearchTerm(company.nom || "");
        setShowResults(false);

        // Auto-fill fields
        setValue("nom", company.nom || "", { shouldValidate: true });
        setValue("siret", company.siret || "", { shouldValidate: true });
        setValue("tvaIntra", company.tvaIntra || "", { shouldValidate: true });
        setValue("adresse", company.adresse || "", { shouldValidate: true });
        setValue("codePostal", company.codePostal || "", { shouldValidate: true });
        setValue("ville", company.ville || "", { shouldValidate: true });

        // Also update country if needed (api defaults to France usually)
        setValue("pays", "France");
        setCountrySearch("France");
    };

    const filteredCountries = COUNTRIES.filter(c =>
        c.toLowerCase().includes(countrySearch.toLowerCase())
    );

    // Old isSaving declaration removed from here

    const onSubmit = async (data: ClientFormValues) => {
        setIsSaving(true);
        setIsSuccess(false);
        try {
            const isNew = !initialData?.id;

            const clientData: Client = {
                id: initialData?.id || "temp",
                societeId: societe?.id || "",

                ...data,
                typeClient: typeEnregistré(typeAprèsSaisie(data.type, data.siret, data.tvaIntra)),
                siret: data.siret,
                tvaIntra: data.tvaIntra,
                adresse2: "",
                pays: countrySearch,
                adressesFacturation: data.adressesFacturation,
            };

            if (isNew) {
                const res = await createClientAction(clientData);
                if (!res.success || !res.id) throw new Error(res.error || t('clients.createError'));
                clientData.id = res.id;
                logAction('create', 'client', `Nouveau client ${clientData.nom} créé`, clientData.id);
            } else {
                const res = await updateClient(clientData);
                if (!res.success) throw new Error(res.error || t('clients.editError'));
                logAction('update', 'client', `Client ${clientData.nom} modifié`, clientData.id);
            }

            await refreshData();
            toast.success(t('clients.saved'));
            setIsSuccess(true);
            reset(data);

            if (onSuccess) {
                onSuccess(clientData);
                return;
            }

            if (isNew) {
                setTimeout(() => {
                    if (returnUrl) {
                        const separator = returnUrl.includes('?') ? '&' : '?';
                        router.push(`${returnUrl}${separator}clientId=${clientData.id}`);
                    } else {
                        router.push("/clients");
                    }
                }, 500);
            }

        } catch (error: any) {
            console.error(error);
            toast.error(t('common.error') + ': ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-12 max-w-5xl mx-auto py-8 px-8">
            <div className="flex items-center justify-between mb-12">
                <div className="flex items-center gap-3">
                    {onCancel ? (
                        <button type="button" onClick={onCancel} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                if (isDirty) {
                                    confirm({
                                        title: t('common.unsavedChanges'),
                                        message: t('common.unsavedChangesConfirm'),
                                        onConfirm: () => router.push("/clients")
                                    });
                                } else {
                                    router.push("/clients");
                                }
                            }}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                    )}
                    <div>
                        <h2 className="text-2xl font-semibold text-foreground">{initialData ? t('clients.editClient') : t('clients.newClient')}</h2>
                        <p className="text-muted-foreground text-sm mt-0.5">Saisissez les informations du client.</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {initialData?.id && onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors border border-blue-500/20 hover:border-blue-500/30"
                        >
                            <FileText className="h-4 w-4" />
                            Voir la fiche client
                        </button>
                    )}

                    <button
                        type="submit"
                        disabled={isSaving || (!!initialData?.id && !isDirty)}
                        className={cn(
                            "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 border",
                            (!isSaving && !(!!initialData?.id && !isDirty)) && "bg-emerald-600 text-white hover:bg-emerald-500 hover:shadow-md border-transparent shadow-sm",
                            isSaving && "bg-emerald-600 text-white opacity-80 cursor-wait border-transparent",
                            (!!initialData?.id && !isDirty && !isSaving) && "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 shadow-none translate-y-[1px]"
                        )}
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Enregistrement...
                            </>
                        ) : (!!initialData?.id && !isDirty) ? (
                            <>
                                <Check className="h-4 w-4" />
                                Enregistré
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                Enregistrer
                            </>
                        )}
                    </button>
                </div>
            </div>



            <div className="space-y-10">
                {/* Type Selector - Subtle Tabs */}
                <div className="flex gap-8 border-b border-border pb-4 mb-10">
                    <button
                        type="button"
                        onClick={() => setValue("type", "societe", { shouldDirty: true })}
                        className={cn(
                            "pb-3 text-sm font-medium transition-all duration-200 relative",
                            watchedType === "societe"
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Société
                        {watchedType === "societe" && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setValue("type", "particulier", { shouldDirty: true })}
                        className={cn(
                            "pb-3 text-sm font-medium transition-all duration-200 relative",
                            watchedType === "particulier"
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Particulier
                        {watchedType === "particulier" && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* Section Identité - Only for Société */}
                    {watchedType === "societe" && (
                        <div className="space-y-6 md:col-span-2">
                            <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-6">
                                <User className="h-4 w-4 text-blue-500" /> Identité de la Société
                            </h3>
                            <div className="space-y-3">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                                    Raison Sociale
                                </label>
                                <div className="space-y-1 relative" ref={searchRef}>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                        <input
                                            {...register("nom", { required: watchedType === "societe" ? t('common.required') : false })}
                                            onChange={(e) => {
                                                register("nom").onChange(e);
                                                setSearchTerm(e.target.value);
                                                if (showResults) setShowResults(false);
                                            }}
                                            className={cn(
                                                "w-full h-11 rounded-lg glass-input pl-10 pr-4 text-foreground focus:ring-2 focus:ring-blue-500/50 transition-all",
                                                duplicateClient && "border-amber-500/50 focus:ring-amber-500/50"
                                            )}
                                            placeholder={t('clients.searchPlaceholder')}
                                            autoComplete="off"
                                        />
                                        {isSearching && (
                                            <div className="absolute right-3 top-3.5">
                                                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Company Search Results */}
                                    {showResults && (isSearching || searchResults.length > 0 || searchError) && (
                                        <div className="absolute top-full left-0 right-0 mt-2 bg-background dark:bg-slate-900 border border-border rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto divide-y divide-border">
                                            {isSearching && (
                                                <div className="px-4 py-3 flex items-center gap-3 text-sm text-muted-foreground animate-pulse">
                                                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                                    Recherche de précisément "{searchTerm}"...
                                                </div>
                                            )}

                                            {!isSearching && searchError && (
                                                <div className="px-4 py-3 flex items-center gap-3 text-sm text-red-400 bg-red-500/5">
                                                    <AlertTriangle className="h-4 w-4" />
                                                    {searchError}
                                                </div>
                                            )}

                                            {!isSearching && !searchError && searchResults.length === 0 && searchTerm.length >= 3 && (
                                                <div className="px-4 py-3 text-sm text-muted-foreground italic">
                                                    Aucun résultat correspondant exactement à "{searchTerm}".
                                                </div>
                                            )}

                                            {!isSearching && searchResults.map((company, index) => (
                                                <button
                                                    key={`${company.siret}-${index}`}
                                                    type="button"
                                                    onClick={() => handleSelectCompany(company)}
                                                    className="w-full text-left px-4 py-3 hover:bg-muted dark:hover:bg-white/5 transition-colors flex flex-col gap-0.5 group"
                                                >
                                                    <span className="font-medium text-foreground group-hover:text-blue-500 transition-colors text-sm">
                                                        {company.nom}
                                                    </span>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        <span>SIRET: {company.siret}</span>
                                                        <span className="opacity-40">|</span>
                                                        <span>{company.ville}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {duplicateClient && (
                                        <div className="flex items-center gap-2 text-amber-500 text-xs animate-in slide-in-from-top-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            <span>Attention : Un client existe déjà avec ce nom</span>
                                        </div>
                                    )}
                                </div>
                                {errors.nom && <span className="text-xs text-red-400">{errors.nom.message}</span>}
                            </div>

                        </div>
                    )}

                    {/* VERSION LATINE — affichée seulement si la fiche en a une.
                        Une adresse française n'a rien à romaniser, et lui
                        offrir ces champs n'apporterait qu'une occasion de se
                        tromper. */}
                    {aUneEcritureOriginale(initialData) && (
                        <div className="space-y-6 md:col-span-2">
                            <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-2">
                                <Languages className="h-4 w-4 text-sky-500" /> Version latine
                                {clientAVerifier(initialData) && (
                                    <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                        À vérifier
                                    </span>
                                )}
                            </h3>
                            {/* Pas de « -mt-4 » ici : le « space-y-6 » du bloc pose
                                déjà l'écart, et la marge négative recopiée des
                                sections voisines collait le paragraphe au titre. */}
                            <p className="text-xs text-muted-foreground max-w-2xl leading-relaxed">
                                C’est elle qui s’affiche dans les listes et s’imprime sur les factures.
                                L’écriture d’origine, elle, est conservée intacte et reste au-dessus.
                                Corriger un champ ici le fige : il ne sera plus régénéré.
                                Le vider le rend au calcul automatique.
                            </p>

                            {[
                                { champ: "nom", libelle: "Raison sociale", latin: "nomLatin" as const, origine: initialData?.nom },
                                { champ: "prenomContact", libelle: "Prénom du contact", latin: "prenomContactLatin" as const, origine: initialData?.prenomContact },
                                { champ: "nomContact", libelle: "Nom du contact", latin: "nomContactLatin" as const, origine: initialData?.nomContact },
                                { champ: "adresse", libelle: "Adresse", latin: "adresseLatin" as const, origine: initialData?.adresse },
                                { champ: "ville", libelle: "Ville", latin: "villeLatin" as const, origine: initialData?.ville },
                            ].filter((c) => !!c.origine).map(({ champ, libelle, latin, origine }) => (
                                <div key={champ} className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                                    <div className="space-y-1">
                                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                            {libelle} — original
                                        </label>
                                        <p className="h-11 flex items-center rounded-lg bg-muted/40 px-4 text-foreground" dir="auto">
                                            {origine}
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                            {libelle} — latin
                                            {champEstVerrouille(initialData, champ) && (
                                                <span className="normal-case font-normal text-[11px] text-sky-600 dark:text-sky-400">
                                                    saisi à la main
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            {...register(latin)}
                                            placeholder="(régénéré automatiquement)"
                                            className="w-full h-11 rounded-lg glass-input px-4 text-foreground"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* SIRET ET TVA, TOUJOURS ATTEIGNABLES.
                        Ils vivaient à l'intérieur du bloc « société », donc
                        invisibles pour un particulier — et invisibles aussi
                        pour une entreprise sans SIRET, qui s'ouvrait en
                        « particulier ». La règle était circulaire : il fallait
                        déjà l'identifiant pour voir la case où l'écrire.
                        Les saisir fait passer en société, ce qui est ce que ces
                        identifiants signifient. */}
                    <div className="space-y-6 md:col-span-2">
                        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-emerald-500" /> Identifiants d’entreprise
                        </h3>
                        <p className="text-xs text-muted-foreground -mt-4">
                            {watchedType === "particulier"
                                ? "Un particulier n’en a normalement pas. En renseigner un fait passer la fiche en société."
                                : "Nécessaires pour adresser une facture électronique à ce client."}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">SIRET</label>
                                <input {...register("siret")} className="w-full h-11 rounded-lg glass-input px-4 text-foreground" />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">N° TVA Intracommunautaire</label>
                                <input {...register("tvaIntra")} className="w-full h-11 rounded-lg glass-input px-4 text-foreground" />
                            </div>
                        </div>
                    </div>


                    <div className="space-y-6">
                        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-6">
                            <Mail className="h-4 w-4 text-purple-500" /> {watchedType === "particulier" ? "Informations" : "Contact"}
                        </h3>
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                        Prénom {watchedType === "particulier" && <span className="text-red-400">*</span>}
                                    </label>
                                    <input
                                        {...register("prenomContact", {
                                            required: watchedType === "particulier" ? "Le prénom est requis" : false
                                        })}
                                        className="w-full h-11 rounded-lg glass-input px-4 text-foreground"
                                    />
                                    {errors.prenomContact && <span className="text-xs text-red-400">{errors.prenomContact.message}</span>}
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                        Nom {watchedType === "particulier" && <span className="text-red-400">*</span>}
                                    </label>
                                    <input
                                        {...register("nomContact", {
                                            required: watchedType === "particulier" ? "Le nom est requis" : false
                                        })}
                                        className="w-full h-11 rounded-lg glass-input px-4 text-foreground"
                                    />
                                    {errors.nomContact && <span className="text-xs text-red-400">{errors.nomContact.message}</span>}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email</label>
                                <input
                                    {...register("email", { required: "L'email est requis" })}
                                    className="w-full h-11 rounded-lg glass-input px-4 text-foreground"
                                />
                                {errors.email && <span className="text-xs text-red-400">{errors.email.message}</span>}
                            </div>

                            <div className="space-y-3">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Téléphone</label>
                                <div className="flex gap-2">
                                    <select className="h-11 rounded-lg glass-input px-2 bg-transparent text-foreground border-r border-white/10 w-24">
                                        <option value="+33">FR +33</option>
                                        <option value="+32">BE +32</option>
                                        <option value="+41">CH +41</option>
                                        <option value="+352">LU +352</option>
                                        <option value="+44">UK +44</option>
                                        <option value="+1">US +1</option>
                                        <option value="+212">MA +212</option>
                                        <option value="+216">TN +216</option>
                                        <option value="+213">DZ +213</option>
                                    </select>
                                    <input {...register("telephone")} className="w-full h-11 rounded-lg glass-input px-4 text-foreground appearance-none" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-6">
                            <MapPin className="h-4 w-4 text-orange-500" /> Adresse
                        </h3>
                        <div className="space-y-8">
                            <div className="space-y-3">
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Adresse</label>
                                {/* On tape, la Base Adresse Nationale propose ; un choix remplit
                                    voie, code postal, ville et pays d'un coup. Le champ reste libre. */}
                                <AdresseAutocomplete
                                    value={watch("adresse") ?? ""}
                                    onChange={(v) => setValue("adresse", v, { shouldDirty: true })}
                                    onSelect={(s) => {
                                        if (s.adresse) setValue("adresse", s.adresse, { shouldDirty: true });
                                        setValue("codePostal", s.codePostal, { shouldDirty: true });
                                        setValue("ville", s.ville, { shouldDirty: true });
                                        setValue("pays", "France", { shouldDirty: true });
                                        setCountrySearch("France");
                                        // Le code postal vient d'être posé avec sa ville : la
                                        // recherche de commune n'a rien à corriger.
                                        codePostalInitial.current = s.codePostal;
                                    }}
                                    className="w-full h-11 rounded-lg glass-input px-4 text-foreground"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Code Postal</label>
                                    <input {...register("codePostal")} className="w-full h-11 rounded-lg glass-input px-4 text-foreground" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ville</label>
                                    <input {...register("ville")} list="communes-du-code-postal" className="w-full h-11 rounded-lg glass-input px-4 text-foreground" />
                                    {communesProposees.length > 1 && (
                                        <>
                                            <datalist id="communes-du-code-postal">
                                                {communesProposees.map((c) => <option key={c} value={c} />)}
                                            </datalist>
                                            <p className="text-[11px] text-muted-foreground">
                                                {communesProposees.length} communes pour ce code postal — choisissez dans la liste si ce n’est pas la bonne.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 relative" ref={dropdownRef}>
                                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pays</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={countrySearch}
                                        onChange={(e) => {
                                            setCountrySearch(e.target.value);
                                            setValue("pays", e.target.value);
                                            setCountryOpen(true);
                                        }}
                                        onFocus={() => setCountryOpen(true)}
                                        className="w-full h-11 rounded-lg glass-input px-4 text-foreground pr-10"
                                        placeholder="Rechercher un pays..."
                                    />
                                    <ChevronDown className="absolute right-3 top-3.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                </div>

                                {countryOpen && (
                                    <div className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto rounded-lg glass-card">
                                        {filteredCountries.length > 0 ? (
                                            filteredCountries.map((country) => (
                                                <button
                                                    key={country}
                                                    type="button"
                                                    onClick={() => {
                                                        setCountrySearch(country);
                                                        setValue("pays", country);
                                                        setCountryOpen(false);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-white/10 transition-colors"
                                                >
                                                    {country}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="p-4 text-sm text-muted-foreground text-center">
                                                Aucun pays trouvé
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* LES AUTRES ADRESSES DE FACTURATION.
                            Amine, 18/09/2026 : « GT Solutions RS, Vitrolles et Paris — je veux
                            pouvoir mettre les deux ; là j'ai dû ajouter un tiret devant l'un
                            des clients ». Une fiche, plusieurs adresses : à la facture de
                            choisir. */}
                        <div className="space-y-4 pt-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-foreground">Autres adresses de facturation</h4>
                                <button
                                    type="button"
                                    onClick={() => adresses.append({ libelle: "", adresse: "", adresse2: "", codePostal: "", ville: "", pays: "France" })}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-500 hover:text-blue-400"
                                >
                                    <Plus className="h-3.5 w-3.5" /> Ajouter une adresse
                                </button>
                            </div>
                            {adresses.fields.length === 0 && (
                                <p className="text-xs text-muted-foreground">
                                    Aucune. L'adresse ci-dessus sert à toutes les factures. Ajoutez-en une si ce client est facturé à plusieurs endroits.
                                </p>
                            )}
                            {adresses.fields.map((champ, index) => (
                                <div key={champ.id} className="rounded-lg border border-border/60 p-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            {...register(`adressesFacturation.${index}.libelle` as const, { required: true })}
                                            placeholder="Nom de l'adresse — ex. Aulnay-sous-Bois"
                                            className="flex-1 h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => adresses.remove(index)}
                                            className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                                            title="Retirer cette adresse"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <AdresseAutocomplete
                                        value={watch(`adressesFacturation.${index}.adresse` as const) ?? ""}
                                        onChange={(v) => setValue(`adressesFacturation.${index}.adresse` as const, v, { shouldDirty: true })}
                                        onSelect={(s) => {
                                            if (s.adresse) setValue(`adressesFacturation.${index}.adresse` as const, s.adresse, { shouldDirty: true });
                                            setValue(`adressesFacturation.${index}.codePostal` as const, s.codePostal, { shouldDirty: true });
                                            setValue(`adressesFacturation.${index}.ville` as const, s.ville, { shouldDirty: true });
                                            setValue(`adressesFacturation.${index}.pays` as const, "France", { shouldDirty: true });
                                        }}
                                        placeholder="Adresse"
                                        className="w-full h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                    />
                                    <input
                                        {...register(`adressesFacturation.${index}.adresse2` as const)}
                                        placeholder="Complément (bâtiment, service…)"
                                        className="w-full h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                    />
                                    <div className="grid grid-cols-3 gap-3">
                                        <input
                                            {...register(`adressesFacturation.${index}.codePostal` as const)}
                                            placeholder="Code postal"
                                            className="h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                        />
                                        <input
                                            {...register(`adressesFacturation.${index}.ville` as const)}
                                            placeholder="Ville"
                                            className="h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                        />
                                        <input
                                            {...register(`adressesFacturation.${index}.pays` as const)}
                                            placeholder="Pays"
                                            className="h-10 rounded-lg glass-input px-3 text-sm text-foreground"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </form >
    );
}
